import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ExternalLink, Smartphone, Sparkles, Monitor, Download, ShoppingBag, Database, CreditCard } from "lucide-react";

export default function PosWoocommerceDetail() {
  const navigate = useNavigate();

  const playStoreUrl = "https://play.google.com/store/apps/details?id=com.pos.hickter.app";
  const demoUrl = "https://posdemo.hickter.com/";

  const features = [
    {
      title: "Real-Time WooCommerce Inventory Sync",
      desc: "Instant bi-directional stock synchronization between your physical retail counter and online WooCommerce store.",
      icon: <ShoppingBag size={20} className="text-emerald-600 dark:text-emerald-400" />
    },
    {
      title: "Multi-Terminal Checkout & Hardware API",
      desc: "Barcode scanner compatibility, thermal receipt printing, cash drawer triggering, and instant UPI QR payments.",
      icon: <CreditCard size={20} className="text-blue-600 dark:text-cyan-400" />
    },
    {
      title: "Multi-Store & Warehouse Stock Management",
      desc: "Manage multiple retail branches, track inventory transfers, set low stock threshold alerts, and manage vendors.",
      icon: <Database size={20} className="text-indigo-600 dark:text-indigo-400" />
    },
    {
      title: "Daily Register Reconciliation & Analytics",
      desc: "End-of-day cash audit reports, cashier shift summaries, top-selling product insights, and profit margin ledger.",
      icon: <Sparkles size={20} className="text-amber-600 dark:text-amber-400" />
    }
  ];

  const techStack = ["WooCommerce", "WordPress", "React", "Node.js", "PHP", "Custom API", "TailwindCSS"];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#030712] text-slate-900 dark:text-slate-100 font-sans pb-32 pt-6 px-4 sm:px-6 lg:px-8 transition-colors duration-300">
      <div className="max-w-5xl mx-auto space-y-8">
        
        {/* TOP BACK BUTTON */}
        <div>
          <button 
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 hover:text-blue-600 dark:hover:text-cyan-400 font-bold text-xs transition-all shadow-sm cursor-pointer group"
          >
            <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
            <span>Back to Products</span>
          </button>
        </div>

        {/* HERO CARD */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 sm:p-10 shadow-xl space-y-8 relative overflow-hidden">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="flex items-center gap-5">
              <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20 p-3 shrink-0 shadow-md flex items-center justify-center">
                <img 
                  src="https://ik.imagekit.io/filennky/Products%20Hickter/posapp.webp?updatedAt=1784371788310" 
                  alt="POS WooCommerce Logo" 
                  className="w-full h-full object-contain rounded-xl"
                />
              </div>
              <div className="space-y-1">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20 text-emerald-600 dark:text-emerald-400 text-xs font-bold">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                  Google PlayStore & WooCommerce Synced
                </div>
                <h1 className="text-2xl sm:text-4xl font-extrabold text-slate-900 dark:text-white tracking-tight">
                  POS Hickter WooCommerce App
                </h1>
                <p className="text-slate-600 dark:text-slate-400 text-xs sm:text-sm font-medium">
                  OmniChannel Point of Sale System & Live Inventory Engine
                </p>
              </div>
            </div>

            {/* ACTION LINKS */}
            <div className="flex items-center gap-3 w-full md:w-auto">
              <a
                href={playStoreUrl}
                target="_blank"
                rel="noreferrer"
                className="btn-primary-blue flex-1 md:flex-initial py-3 px-5 text-xs font-bold rounded-xl flex items-center justify-center gap-2 !text-white shadow-lg shadow-blue-500/20"
              >
                <Smartphone size={16} className="!text-white" />
                <span className="!text-white">PlayStore</span>
                <ExternalLink size={13} className="!text-white" />
              </a>

              <a
                href={demoUrl}
                target="_blank"
                rel="noreferrer"
                className="flex-1 md:flex-initial py-3 px-5 text-xs font-extrabold rounded-xl bg-black dark:bg-black text-white !text-white border border-slate-800 flex items-center justify-center gap-2 shadow-md hover:bg-slate-900 transition-colors"
              >
                <Monitor size={16} className="!text-white" />
                <span className="!text-white font-extrabold">POS Demo</span>
              </a>
            </div>
          </div>

          {/* OVERVIEW SECTION */}
          <div className="space-y-3 pt-4 border-t border-slate-100 dark:border-slate-800">
            <h3 className="text-base font-extrabold text-slate-900 dark:text-white">Product Overview</h3>
            <p className="text-slate-600 dark:text-slate-300 text-xs sm:text-sm leading-relaxed font-medium">
              POS Hickter WooCommerce App is an all-in-one point of sale software designed to empower retail stores, supermarkets, fashion boutiques, and multi-location businesses. It bridges the gap between your physical retail counter and online WooCommerce e-commerce storefront with sub-second inventory sync and instant receipt printing.
            </p>
          </div>

          {/* TECH STACK BADGES */}
          <div className="space-y-3 pt-2">
            <h4 className="text-xs font-extrabold uppercase tracking-wider text-slate-400 dark:text-slate-500">
              Built With Modern Stack:
            </h4>
            <div className="flex flex-wrap gap-2">
              {techStack.map((tech, idx) => (
                <span key={idx} className="px-3 py-1 rounded-xl bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20 text-emerald-700 dark:text-emerald-400 text-xs font-bold">
                  #{tech}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* CORE FEATURES GRID */}
        <div className="space-y-4">
          <h2 className="text-xl font-extrabold text-slate-900 dark:text-white">Core Capabilities & Modules</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {features.map((feat, idx) => (
              <div 
                key={idx}
                className="p-5 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-md space-y-2"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-xl bg-slate-100 dark:bg-slate-800">
                    {feat.icon}
                  </div>
                  <h4 className="text-sm font-extrabold text-slate-900 dark:text-white">{feat.title}</h4>
                </div>
                <p className="text-xs text-slate-600 dark:text-slate-400 font-medium leading-relaxed">
                  {feat.desc}
                </p>
              </div>
            ))}
          </div>
        </div>

      </div>

      {/* FIXED BOTTOM DOWNLOAD & ACCESS BAR */}
      <div className="fixed bottom-0 left-0 right-0 z-[110] bg-white/95 dark:bg-[#030712]/95 backdrop-blur-xl border-t border-slate-200 dark:border-slate-800 p-3 sm:p-4 shadow-2xl">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2.5 sm:gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20 p-1.5 shrink-0 hidden sm:flex items-center justify-center">
              <img 
                src="https://ik.imagekit.io/filennky/Products%20Hickter/posapp.webp?updatedAt=1784371788310" 
                alt="POS Logo" 
                className="w-full h-full object-contain rounded-lg"
              />
            </div>
            <div className="text-left">
              <h4 className="text-xs sm:text-sm font-extrabold text-slate-900 dark:text-white truncate">POS Hickter WooCommerce App</h4>
              <p className="text-[10px] text-slate-500 dark:text-slate-400 font-medium hidden sm:block">Google PlayStore & Cloud POS System</p>
            </div>
          </div>

          <div className="flex items-center gap-2.5 w-full sm:w-auto shrink-0">
            <a
              href={playStoreUrl}
              target="_blank"
              rel="noreferrer"
              className="flex-1 sm:flex-initial py-2.5 px-5 rounded-xl bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-700 hover:from-blue-500 hover:to-indigo-600 text-white font-extrabold text-xs flex items-center justify-center gap-2 transition-all shadow-md shadow-blue-500/25 !text-white"
            >
              <Download size={15} className="!text-white" />
              <span className="!text-white font-black">Get App</span>
            </a>

            <a
              href={demoUrl}
              target="_blank"
              rel="noreferrer"
              className="flex-1 sm:flex-initial py-2.5 px-4 rounded-xl bg-black dark:bg-black text-white !text-white border border-slate-800 text-xs font-extrabold flex items-center justify-center gap-2 shadow-sm hover:bg-slate-900 transition-colors"
            >
              <Monitor size={15} className="!text-white" />
              <span className="!text-white font-extrabold">POS Demo</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
